/**
 * Mentions travel as `@[Name](userId)` tokens (the API's format). While typing,
 * the box shows the readable `@Name` and keeps the ids aside; sending turns
 * the text back into tokens.
 */

const TOKEN =
  /@\[([^\]\n]{1,80})\]\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/gi;

export type Mentioned = { id: string; name: string };
export type BodyPart = { text: string } | { mention: Mentioned };

/** A comment body as text runs and mentions, for rendering. */
export function parseBody(body: string): BodyPart[] {
  const parts: BodyPart[] = [];
  let last = 0;
  for (const match of body.matchAll(TOKEN)) {
    if (match.index > last) parts.push({ text: body.slice(last, match.index) });
    parts.push({ mention: { name: match[1]!, id: match[2]! } });
    last = match.index + match[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last) });
  return parts;
}

/** Token body → what the person edits (`@Name`), plus who it mentions. */
export function toEditable(body: string): { text: string; mentions: Mentioned[] } {
  const mentions: Mentioned[] = [];
  const text = body.replace(TOKEN, (_match, name: string, id: string) => {
    if (!mentions.some((m) => m.id === id)) mentions.push({ id, name });
    return `@${name}`;
  });
  return { text, mentions };
}

/** What the person typed → token body. Only `@Name`s still in the text count. */
export function toTokens(text: string, mentions: Mentioned[]): string {
  // Longest names first, so "@Maya Kowalski" wins over a "@Maya".
  const byLength = [...mentions].sort((a, b) => b.name.length - a.name.length);
  let out = '';
  let i = 0;
  while (i < text.length) {
    const hit =
      text[i] === '@' && (i === 0 || /\s/.test(text[i - 1]!))
        ? byLength.find((m) => text.startsWith(`@${m.name}`, i))
        : undefined;
    if (hit) {
      out += `@[${hit.name}](${hit.id})`;
      i += hit.name.length + 1;
    } else {
      out += text[i];
      i += 1;
    }
  }
  return out;
}

/** The "@query" being typed at the caret, if any. */
export function activeMention(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const query = before.slice(at + 1);
  // Names have one space at most ("Maya Kowalski"); a newline ends the mention.
  if (/\n/.test(query) || (query.match(/ /g) ?? []).length > 1 || query.length > 40) {
    return null;
  }
  return { start: at, query };
}

/** Replaces the "@query" at `start..caret` with the chosen name. */
export function insertMention(
  text: string,
  start: number,
  caret: number,
  name: string,
): { text: string; caret: number } {
  const inserted = `@${name} `;
  return {
    text: text.slice(0, start) + inserted + text.slice(caret),
    caret: start + inserted.length,
  };
}
